import { useCallback, useEffect, useRef, useState } from 'react'
import type { AgentRun } from '@/api/proposals/proposals'
import { mapErrorMessage, mapProposalErrorMessage } from './errorMessages'

const POLL_DELAY_MS = 2_000
const terminalStatuses = new Set<AgentRun['status']>(['succeeded', 'failed', 'cancelled'])

function isTerminal(run: AgentRun): boolean {
  return terminalStatuses.has(run.status)
}

function failureKey(run: AgentRun | null): string | null {
  return run?.status === 'failed'
    ? JSON.stringify([run.runId, run.updatedAtTsMs, run.failureCode, run.failureMessage])
    : null
}

function transportErrorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : 'An error occurred'
  const mapped = mapErrorMessage(raw)
  return mapped.code === 'UNKNOWN' ? mapProposalErrorMessage(raw).message : mapped.message
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

export interface UseBuildRunOptions {
  initialRun: AgentRun | null
  getRun: (runId: number, signal: AbortSignal) => Promise<AgentRun>
  startRun: () => Promise<AgentRun>
  cancelRun: (runId: number) => Promise<AgentRun>
  retryRun: (runId: number) => Promise<AgentRun>
  onProgress?: (run: AgentRun) => void
}

export interface UseBuildRunResult {
  run: AgentRun | null
  error: string | null
  operationError: string | null
  failureMessage: string | null
  isStarting: boolean
  isCancelling: boolean
  isRetrying: boolean
  start: () => Promise<void>
  cancel: () => Promise<void>
  retry: () => Promise<void>
  clearError: () => void
}

export function useBuildRun({
  initialRun,
  getRun,
  startRun,
  cancelRun,
  retryRun,
  onProgress
}: UseBuildRunOptions): UseBuildRunResult {
  const [run, setRun] = useState<AgentRun | null>(initialRun)
  const [operationError, setOperationError] = useState<string | null>(null)
  const [dismissedFailure, setDismissedFailure] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [pollEpoch, setPollEpoch] = useState(0)

  const mountedRef = useRef(true)
  const generationRef = useRef(0)
  const commandOwnerRef = useRef<object | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAbortRef = useRef<AbortController | null>(null)
  const runRef = useRef<AgentRun | null>(initialRun)
  const scopeRef = useRef(getRun)
  const getRunRef = useRef(getRun)
  const onProgressRef = useRef(onProgress)
  getRunRef.current = getRun
  onProgressRef.current = onProgress

  const clearPollTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const stopPolling = useCallback(() => {
    clearPollTimer()
    pollAbortRef.current?.abort()
    pollAbortRef.current = null
  }, [clearPollTimer])

  const replaceRun = useCallback((next: AgentRun, reportProgress: boolean) => {
    const previous = runRef.current
    runRef.current = next
    setRun(next)
    setOperationError(null)
    if (reportProgress && previous?.runId === next.runId && next.completedSteps > previous.completedSteps) {
      onProgressRef.current?.(next)
    }
  }, [])

  const claimCommand = useCallback(() => {
    if (commandOwnerRef.current !== null) return null
    setDismissedFailure(null)
    const owner = {}
    commandOwnerRef.current = owner
    return { owner, generation: ++generationRef.current }
  }, [])

  const releaseCommand = useCallback((owner: object) => {
    if (commandOwnerRef.current !== owner) return false
    commandOwnerRef.current = null
    return true
  }, [])

  useEffect(() => {
    if (scopeRef.current !== getRun) {
      scopeRef.current = getRun
      generationRef.current += 1
      commandOwnerRef.current = null
      stopPolling()
      runRef.current = initialRun
      setRun(initialRun)
      setOperationError(null)
      setDismissedFailure(null)
      setIsStarting(false)
      setIsCancelling(false)
      setIsRetrying(false)
      setPollEpoch((epoch) => epoch + 1)
      return
    }

    const current = runRef.current
    if (initialRun === null) return

    const isNewerSameRun = current?.runId === initialRun.runId && initialRun.updatedAtTsMs > current.updatedAtTsMs
    const isNewerRun =
      current !== null && initialRun.runId !== current.runId && initialRun.createdOnTsMs > current.createdOnTsMs
    if (current === null || isNewerSameRun || isNewerRun) {
      generationRef.current += 1
      commandOwnerRef.current = null
      stopPolling()
      replaceRun(initialRun, false)
      setIsStarting(false)
      setIsCancelling(false)
      setIsRetrying(false)
      setPollEpoch((epoch) => epoch + 1)
    }
  }, [getRun, initialRun, stopPolling, replaceRun])

  useEffect(() => {
    const current = runRef.current
    if (!current || isTerminal(current)) return

    const runId = current.runId
    const generation = generationRef.current
    let disposed = false

    const schedule = () => {
      clearPollTimer()
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        void poll()
      }, POLL_DELAY_MS)
    }

    const poll = async () => {
      const controller = new AbortController()
      pollAbortRef.current = controller
      try {
        const next = await getRunRef.current(runId, controller.signal)
        if (pollAbortRef.current === controller) pollAbortRef.current = null
        if (disposed || !mountedRef.current || generationRef.current !== generation) return
        replaceRun(next, true)
        if (!isTerminal(next)) schedule()
      } catch (error) {
        if (pollAbortRef.current === controller) pollAbortRef.current = null
        if (isAbortError(error)) return
        if (disposed || !mountedRef.current || generationRef.current !== generation) return
        setOperationError(transportErrorMessage(error))
        const latest = runRef.current
        if (latest?.runId === runId && !isTerminal(latest)) schedule()
      }
    }

    schedule()
    return () => {
      disposed = true
      stopPolling()
    }
  }, [run?.runId, run?.status, run?.cancelRequested, pollEpoch, clearPollTimer, stopPolling, replaceRun])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      commandOwnerRef.current = null
      stopPolling()
    }
  }, [stopPolling])

  const start = useCallback(async () => {
    const command = claimCommand()
    if (command === null) return
    const { owner, generation } = command
    stopPolling()
    setOperationError(null)
    setIsStarting(true)
    try {
      const accepted = await startRun()
      if (!mountedRef.current || generationRef.current !== generation) return
      replaceRun(accepted, false)
    } catch (error) {
      if (!mountedRef.current || generationRef.current !== generation) return
      setOperationError(transportErrorMessage(error))
    } finally {
      if (releaseCommand(owner) && mountedRef.current && generationRef.current === generation) {
        setIsStarting(false)
        setPollEpoch((epoch) => epoch + 1)
      }
    }
  }, [startRun, stopPolling, replaceRun, claimCommand, releaseCommand])

  const cancel = useCallback(async () => {
    const current = runRef.current
    if (!current || isTerminal(current)) return
    const command = claimCommand()
    if (command === null) return
    const { owner, generation } = command
    stopPolling()
    setOperationError(null)
    setIsCancelling(true)
    try {
      const cancelling = await cancelRun(current.runId)
      if (!mountedRef.current || generationRef.current !== generation) return
      replaceRun(cancelling, true)
    } catch (error) {
      if (!mountedRef.current || generationRef.current !== generation) return
      setOperationError(transportErrorMessage(error))
    } finally {
      if (releaseCommand(owner) && mountedRef.current && generationRef.current === generation) {
        setIsCancelling(false)
        setPollEpoch((epoch) => epoch + 1)
      }
    }
  }, [cancelRun, stopPolling, replaceRun, claimCommand, releaseCommand])

  const retry = useCallback(async () => {
    const current = runRef.current
    if (!current || current.status !== 'failed') return
    const command = claimCommand()
    if (command === null) return
    const { owner, generation } = command
    stopPolling()
    setOperationError(null)
    setIsRetrying(true)
    try {
      const accepted = await retryRun(current.runId)
      if (!mountedRef.current || generationRef.current !== generation) return
      replaceRun(accepted, false)
    } catch (error) {
      if (!mountedRef.current || generationRef.current !== generation) return
      setOperationError(transportErrorMessage(error))
    } finally {
      if (releaseCommand(owner) && mountedRef.current && generationRef.current === generation) {
        setIsRetrying(false)
        setPollEpoch((epoch) => epoch + 1)
      }
    }
  }, [retryRun, stopPolling, replaceRun, claimCommand, releaseCommand])

  const clearError = useCallback(() => {
    setOperationError(null)
    setDismissedFailure(failureKey(runRef.current))
  }, [])
  const failureMessage = run?.status === 'failed' ? run.failureMessage : null

  return {
    run,
    error: operationError ?? (dismissedFailure === failureKey(run) ? null : failureMessage),
    operationError,
    failureMessage,
    isStarting,
    isCancelling,
    isRetrying,
    start,
    cancel,
    retry,
    clearError
  }
}
